import { orderBy } from "es-toolkit";
import { makeAutoObservable, runInAction } from "mobx";

import {
  createDocumentPermission,
  deleteDocumentPermission,
  getDocumentPermissionsAndRequests,
  solicitDocumentPermission,
  updateDocumentPermission,
  type DocumentPermission,
  type TeamShort,
  acceptDocumentPermission,
  resentDocumentPermissionMail,
} from "@/api";

import type { DocumentModel } from "../DocumentModel";

export type Scope = "read" | "comment" | "write";

export type PermissionToRender = {
  team?: TeamShort;
  email?: string;
  access?: DocumentPermission;
  request?: DocumentPermission;
};

export class DocumentPermissionsModel {
  doc: DocumentModel;

  scopeToGive: Scope = "read";

  permissions: DocumentPermission[] = [];

  emails: DocumentPermission[] = [];

  loadStatus: "init" | "loading" | "loaded" = "init";

  constructor(doc: DocumentModel) {
    this.doc = doc;

    makeAutoObservable<DocumentPermissionsModel>(this);
  }

  get write() {
    return this.doc.api.permissions.write;
  }

  get read() {
    return this.doc.api.permissions.read;
  }

  get comment() {
    return this.doc.api.permissions.comment;
  }

  get requestScope() {
    return this.doc.api.permissionRequestScope;
  }

  setPermissionToGive(scope: Scope) {
    this.scopeToGive = scope;
  }

  async createPermission(
    query: { permalink: string } | { email: string },
    scope: Scope,
    requestId?: string,
    existingPermission?: DocumentPermission,
  ) {
    const docId = this.doc?.id;
    if (!docId) {
      console.error("createPermission: No document id");
      return;
    }

    if (requestId && existingPermission) {
      const res = await this.updatePermission(existingPermission, scope);
      await this.deletePermission(requestId);
      return res;
    }

    const res = await createDocumentPermission({
      uid: docId,
      permalink: "permalink" in query ? query.permalink : undefined,
      email: "email" in query ? query.email : undefined,
      scope,
    });

    runInAction(() => {
      if (res.document_permission?.team) {
        this.permissions.push(res.document_permission);
      }
      if (res.document_permission?.email) {
        this.emails.push(res.document_permission);
      }
    });

    return res;
  }

  async requestPermission(scope: Scope) {
    if (!this.doc?.id) {
      console.error("requestPermission: No document id");
      return;
    }
    const res = await solicitDocumentPermission({
      uid: this.doc?.id,
      scope,
    });
    return res;
  }

  async updatePermission(permission: DocumentPermission, newScope: Scope) {
    if (newScope === permission.scope) {
      return;
    }
    const res = await updateDocumentPermission(permission.uid, {
      scope: newScope,
    });
    return res;
  }

  async deletePermission(id: string): Promise<void> {
    await deleteDocumentPermission(id);
    this.permissions = this.permissions.filter((permission) => permission.uid !== id);
    this.emails = this.emails.filter((permission) => permission.uid !== id);
  }

  async acceptPermission(token: string) {
    const res = await acceptDocumentPermission({ token });
    return res;
  }

  async resendEmailPermission(uid: string) {
    const resend = resentDocumentPermissionMail as (uid: string) => Promise<unknown>;
    await resend(uid);
  }

  async fetchPermissionsAndRequests(signal?: AbortSignal) {
    if (!this.doc?.id) {
      console.error("fetchPermissionsAndRequests: No document id");
      return;
    }
    this.loadStatus = "loading";
    try {
      const res = await getDocumentPermissionsAndRequests(this.doc?.id, { signal });

      runInAction(() => {
        if (res) {
          const emailsConfirmed = res.confirmed?.filter(
            (permission) => permission.email && !permission.team,
          );
          this.emails = emailsConfirmed || [];
          this.permissions = [...(res.confirmed || []), ...(res.unconfirmed || [])];
        }
      });

      return res;
    } finally {
      runInAction(() => {
        this.loadStatus = "loaded";
      });
    }
  }

  get permissionsToRender(): PermissionToRender[] {
    const merged = new Map<string, PermissionToRender>();

    for (const permission of this.permissions || []) {
      const key = permission.team?.permalink ?? permission.email ?? permission.uid;
      const value = merged.get(key) ?? {
        team: permission.team,
        email: permission.email ?? undefined,
      };

      if (permission.confirmed) {
        value.access = permission;
      } else {
        value.request = permission;
      }
      merged.set(key, value);
    }

    const entries = Array.from(merged.values());
    return orderBy(
      entries,
      [({ request }) => !!request, ({ team, email }) => team?.permalink ?? email ?? ""],
      ["desc", "asc"],
    );
  }
}
